import Layout from '../../components/Layout';
import ProductCard from '../../components/ProductCard';
import DisclaimerNotice from '../../components/DisclaimerNotice';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const products = [
  {
    name: "Lightweight Tinted Moisturizer",
    description: "Light coverage with skincare benefits—perfect for everyday wear.",
    bestFor: "All Skin Types",
    benefits: ["Buildable coverage", "SPF included", "Skincare-makeup hybrid"]
  },
  {
    name: "Cream Concealer",
    description: "Blendable formula to cover dark circles and minor blemishes.",
    bestFor: "All Skin Types",
    benefits: ["Natural finish", "Doesn't crease", "Long-lasting wear"]
  },
  {
    name: "Natural Brow Gel",
    description: "Tinted gel that shapes and fills brows with a natural finish.",
    bestFor: "All Brow Types",
    benefits: ["Easy application", "Natural look", "All-day hold"]
  },
  {
    name: "Lengthening Mascara",
    description: "Defines and lengthens lashes without clumping.",
    bestFor: "Daily Wear",
    benefits: ["No clumping", "Buildable formula", "Easy to remove"]
  },
  {
    name: "Cream Blush Stick",
    description: "Easy-to-apply formula that blends beautifully into skin.",
    bestFor: "All Skin Types",
    benefits: ["Blend with fingers", "Natural flush", "Mistake-proof application"]
  },
  {
    name: "Tinted Lip Balm",
    description: "Hydrating color that's effortless and comfortable to wear.",
    bestFor: "Daily Wear",
    benefits: ["Hydrating formula", "Sheer color", "No touch-ups needed"]
  }
];

const MakeupEveryday = () => {
  return (
    <Layout>
      {/* Hero */}
      <section data-testid="everyday-hero" className="py-20 md:py-32 bg-[#F2EBE3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link 
            to="/makeup" 
            className="inline-flex items-center gap-2 text-[#3A4D39] text-sm font-medium mb-6 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Makeup
          </Link>
          <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
            Makeup Looks
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1C1C1C] leading-tight mb-6">
            Everyday Makeup Looks
          </h1>
          <p className="text-base md:text-lg text-[#374151] leading-relaxed">
            Simple, flattering products for your daily routine. These picks work for office, errands, or any day when you want to look polished without spending too much time.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-4">
            The Everyday Philosophy
          </h2>
          <p className="text-[#374151] leading-relaxed mb-4">
            Everyday makeup should be quick, comfortable, and forgiving. The goal is to enhance—not transform—your natural features. Products that blend easily and don't require precision are key.
          </p>
          <p className="text-[#374151] leading-relaxed">
            A full everyday look can be done in under 10 minutes with the right products.
          </p>
        </div>
      </section>

      {/* Products */}
      <section data-testid="everyday-products" className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-8">
            Recommended Products
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {products.map((product) => (
              <ProductCard key={product.name} {...product} />
            ))}
          </div>
        </div>
      </section>

      {/* How to Choose */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-6">
            How to Choose
          </h2>
          <ul className="space-y-4 text-[#374151]">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Prioritize ease:</strong> Cream and stick formulas are more forgiving than powders.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Match your undertone:</strong> Cool, warm, or neutral—the right undertone makes products look natural.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Test in natural light:</strong> Store lighting can be deceiving. Check products near a window if possible.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Consider your skin type:</strong> Oily skin may prefer matte formulas; dry skin may prefer dewy.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-[#F5F5F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <DisclaimerNotice variant="inline" />
        </div>
      </section>
    </Layout>
  );
};

export default MakeupEveryday;
